import React from 'react';
import ReactDOM from 'react-dom/client';
import '@fortawesome/fontawesome-free/css/all.min.css';
import App from './App';
import {
  installPerformanceDiagnostics,
  markPerformance,
  setPerformanceContext,
  startPerformanceSpan
} from './services/performanceTelemetry';
import { loadBrowserPlatformRuntime } from './services/platformRuntimeLoader';

async function startDeferredPlatformBridge() {
  await loadBrowserPlatformRuntime();
  const { ensurePlatformBridge } = await import('./platformBridge');
  return ensurePlatformBridge();
}

export function createPlatformBridgeController(startBridge = startDeferredPlatformBridge) {
  let started = false;
  let resolveReady;
  let rejectReady;
  const ready = new Promise((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });
  const start = () => {
    if (started) return ready;
    started = true;
    const finishPlatformBootstrap = startPerformanceSpan('platform-bridge-bootstrap');
    Promise.resolve()
      .then(startBridge)
      .then(() => {
        finishPlatformBootstrap();
        resolveReady();
      })
      .catch((error) => {
        finishPlatformBootstrap({
          status: 'error',
          errorCode: String(
            error?.code || error?.message || 'platform_bootstrap_failed'
          ).slice(0, 120)
        });
        rejectReady(error);
      });
    return ready;
  };
  return { ready, start, isStarted: () => started };
}

function bootstrap() {
  markPerformance('app_entry');
  setPerformanceContext({
    route: window.location?.pathname || '/',
    online: typeof navigator === 'undefined' ? null : navigator.onLine !== false
  });
  installPerformanceDiagnostics();
  markPerformance('shell_render_start');
  const platformBridge = createPlatformBridgeController();
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <React.StrictMode>
      <App
        platformReadyPromise={platformBridge.ready}
        startPlatformBridge={platformBridge.start}
      />
    </React.StrictMode>
  );
  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => markPerformance('shell_visible'));
  } else {
    markPerformance('shell_visible');
  }

  if (window.electron?.runtime === 'electron') {
    void platformBridge.start();
  }
}

bootstrap();
